export const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export type Ability = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export const ABILITIES: { value: Ability; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' },
];

/**
 * Mirrors public.destination_monthly_intelligence exactly. mean_deep_water_swell_height_m
 * is offshore/deep-water swell height -- never breaking wave face height (see
 * supabase/migrations/20260819120000_create_destination_monthly_intelligence.sql). It and
 * its three siblings below are reserved for future technical/scientific use and are NULL
 * for the entire V1 dataset -- no user-facing code may read them. Use
 * typicalBreakingWaveFaceHeightM (populated) for any consumer-facing wave-size display.
 */
export interface MonthlyIntelligenceRow {
  month: number;
  /** Reserved technical field, NULL in V1 -- never read this for UI. */
  meanDeepWaterSwellHeightM: number | null;
  /** Reserved technical field, NULL in V1 -- never read this for UI. */
  meanSwellPeriodS: number | null;
  /** Reserved technical field, NULL in V1 -- never read this for UI. */
  groundswellFrequencyPct: number | null;
  /** Reserved technical field, NULL in V1 -- never read this for UI. */
  favourableWindFrequencyPct: number | null;
  prevailingWindDirection: string | null;
  meanWaterTemperatureC: number | null;
  meanAirTemperatureC: number | null;
  meanDailyHighC: number | null;
  meanDailyLowC: number | null;
  monthlyRainfallMm: number | null;
  rainDays: number | null;
  surfConsistencyScore: number | null;
  windQualityScore: number | null;
  /** Higher = more crowded (worse). Confirmed against known destination/month
   * research values -- see CROWD_DIRECTION_EVIDENCE in this file. Never invert
   * a second time in a consuming layer. */
  crowdPressureScore: number | null;
  beginnerSuitability: number | null;
  intermediateSuitability: number | null;
  advancedSuitability: number | null;
  expertSuitability: number | null;
  /** Researched signal for "this month is a strong overall trade-off," not a
   * before/after-peak-season inference. See isShoulderSeasonMonth. */
  shoulderSeasonValue: number | null;
  comfortScore: number | null;
  overallConfidence: string | null;
  /** Independent researched product metric -- the % of days in the month with
   * usable surf somewhere in the destination. Never derive this from
   * surfConsistencyScore (or vice versa); the two are deliberately separate
   * research questions. */
  surfableDaysPct: number | null;
  surfableDaysPctConfidence: string | null;
  /** Consumer-facing estimate of actual breaking wave face height -- distinct from
   * meanDeepWaterSwellHeightM (offshore/deep-water swell) above. Never derive one
   * from the other, and never label this "swell height" in UI copy. */
  typicalBreakingWaveFaceHeightM: number | null;
  typicalBreakingWaveFaceHeightConfidence: string | null;
}

/** The stored, researched suitability score for one ability -- never recalculated
 * from swell height or any other field. */
export const getSuitabilityForAbility = (row: MonthlyIntelligenceRow, ability: Ability): number | null => {
  switch (ability) {
    case 'beginner':
      return row.beginnerSuitability;
    case 'intermediate':
      return row.intermediateSuitability;
    case 'advanced':
      return row.advancedSuitability;
    case 'expert':
      return row.expertSuitability;
  }
};

/** A month is a notable shoulder-season signal at shoulder_season_value_1_10 >= 7 --
 * matching the founder's explicit threshold, not an invented one. Surfaced in the
 * destination-detail month panel and in recommendation-reasons.ts for destinations
 * with monthly intelligence, superseding the old adjacent-to-best-months heuristic
 * for those destinations. */
export const SHOULDER_SEASON_THRESHOLD = 7;

export const isShoulderSeasonMonth = (row: MonthlyIntelligenceRow): boolean =>
  row.shoulderSeasonValue !== null && row.shoulderSeasonValue >= SHOULDER_SEASON_THRESHOLD;

/**
 * CROWD DIRECTION -- verified, not assumed from the field name.
 *
 * crowd_pressure_score_1_10: higher = more crowded (worse). Confirmed against real
 * production research values across three unrelated destinations with well-known,
 * independently verifiable seasonal tourism patterns (not by field name alone):
 *
 *  - Santa Teresa (Costa Rica): dry/peak-tourist Jan/Feb scores 9; wet/quiet
 *    Sep/Oct scores 3.
 *  - Ericeira (Portugal): peak beach-tourism Jul/Aug scores 9 despite winter
 *    (Jan/Feb) having the destination's *best* surf_consistency_score (9) --
 *    crowding tracks general tourist season, not surf quality, and moves the
 *    same direction (higher score in the objectively busier months regardless
 *    of surf conditions).
 *  - Siargao (Philippines): peak surf-tourist season Sep/Oct (also its best
 *    surf_consistency_score, 9-10) scores 8-9; quieter Jan-Jun scores 3-6.
 *
 * All three move the same direction, matching the existing destinations.crowd_score
 * convention already documented in get_top_destination_matches_v3 ("Crowd score
 * means 10 = busiest"). Locked in as an executable assertion in
 * destination-monthly-intelligence.test.ts using these exact fixture values.
 *
 * Do not invert crowdPressureScore a second time anywhere else in the app --
 * getCrowdLevel below is the one place that turns the raw score into a
 * direction-aware bucket; build "quieter than usual" style copy from its output,
 * not from a fresh inversion of the raw number.
 */
export type CrowdLevel = 'quiet' | 'moderate' | 'busy' | 'unknown';

export const getCrowdLevel = (score: number | null): CrowdLevel => {
  if (score === null) return 'unknown';
  if (score <= 3) return 'quiet';
  if (score <= 6) return 'moderate';
  return 'busy';
};

/**
 * Compares one month's crowd pressure against the same destination's own highest
 * month (never across destinations -- crowd pressure is destination-relative, e.g.
 * Santa Teresa's "busy" and a quiet reef pass's "busy" are not comparable numbers).
 * Used for copy like "usually quieter than peak season."
 */
export const isQuieterThanPeakSeason = (row: MonthlyIntelligenceRow, allMonths: MonthlyIntelligenceRow[]): boolean => {
  if (row.crowdPressureScore === null) return false;
  const peak = allMonths.reduce<number | null>(
    (max, m) => (m.crowdPressureScore === null ? max : max === null ? m.crowdPressureScore : Math.max(max, m.crowdPressureScore)),
    null,
  );
  return peak !== null && row.crowdPressureScore < peak;
};

/**
 * The approved, consumer-facing breaking-wave-height estimate -- never derived
 * from or substituted with meanDeepWaterSwellHeightM (offshore/deep-water swell).
 * Added once real values were supplied (see
 * supabase/migrations/20260811054904_add_breaking_wave_face_height_to_destination_monthly_intelligence.sql).
 */
export const getBreakingWaveHeightM = (row: MonthlyIntelligenceRow): number | null =>
  row.typicalBreakingWaveFaceHeightM;

export interface ChartMonth {
  month: number;
  monthLabel: string;
  suitability: number | null;
  consistency: number | null;
  waveHeightM: number | null;
  surfableDaysPct: number | null;
  row: MonthlyIntelligenceRow | null;
}

/**
 * Always returns exactly 12 entries in Jan-Dec order regardless of the input
 * array's own order or completeness -- a destination with fewer than 12 rows
 * (or rows out of order) still renders a correct, gap-aware 12-month axis rather
 * than a squeezed or reordered one.
 */
export const buildChartMonths = (rows: MonthlyIntelligenceRow[], ability: Ability): ChartMonth[] => {
  const byMonth = new Map(rows.map((r) => [r.month, r]));

  return MONTH_ABBR.map((label, idx) => {
    const month = idx + 1;
    const row = byMonth.get(month) ?? null;
    return {
      month,
      monthLabel: label,
      suitability: row ? getSuitabilityForAbility(row, ability) : null,
      consistency: row ? row.surfConsistencyScore : null,
      waveHeightM: row ? getBreakingWaveHeightM(row) : null,
      surfableDaysPct: row ? row.surfableDaysPct : null,
      row,
    };
  });
};

/** True only when at least one month has approved breaking-wave-height data --
 * the Wave Size row must render fully or not at all, never a partial/empty shell. */
export const hasWaveSizeData = (chartMonths: ChartMonth[]): boolean =>
  chartMonths.some((m) => m.waveHeightM !== null);

export interface BestTimeWindow {
  /** 1-based, inclusive */
  startMonth: number;
  /** 1-based, inclusive */
  endMonth: number;
}

/**
 * Display-only rule, computed purely from the already-approved FOR YOU
 * suitability scores -- never modifies, smooths, or invents a score:
 *
 * 1. Find the highest suitability score across the 12 months for the selected
 *    ability. If every month is null, or the highest score is below 7, there is
 *    no "best time" window.
 * 2. Find the longest run of *consecutive* months that sit exactly at that peak
 *    score. Ties for longest run are broken by whichever run occurs earliest
 *    in the year.
 *
 * This never wraps across the Dec/Jan boundary -- it reads left-to-right across
 * the same Jan-Dec grid the user sees, so the highlighted window always matches
 * what's visually in front of them.
 */
export const findBestTimeWindow = (chartMonths: ChartMonth[]): BestTimeWindow | null => {
  const scores = chartMonths.map((m) => m.suitability);
  const peak = scores.reduce<number | null>(
    (max, score) => (score === null ? max : max === null ? score : Math.max(max, score)),
    null,
  );
  if (peak === null || peak < 7) return null;

  let bestStart = -1;
  let bestLength = 0;
  let runStart = -1;
  let runLength = 0;

  scores.forEach((score, i) => {
    if (score === peak) {
      if (runLength === 0) runStart = i;
      runLength += 1;
    } else {
      runLength = 0;
    }
    if (runLength > bestLength) {
      bestLength = runLength;
      bestStart = runStart;
    }
  });

  if (bestStart === -1) return null;
  return { startMonth: bestStart + 1, endMonth: bestStart + bestLength };
};

/**
 * Builds an SVG path string for a smoothed line through up to 12 monthly
 * values, using each pair's midpoint as a quadratic-curve endpoint. A null
 * value breaks the line into a separate segment rather than interpolating
 * across a genuine data gap -- never invents a value to bridge one.
 */
export const buildSmoothPath = (
  values: (number | null)[],
  width: number,
  height: number,
  minValue: number,
  maxValue: number,
): string => {
  const stepX = width / Math.max(values.length - 1, 1);
  const range = Math.max(maxValue - minValue, 0.0001);
  const toY = (v: number) => height - ((v - minValue) / range) * height;

  const points = values.map((v, i) => (v === null ? null : { x: i * stepX, y: toY(v) }));

  const segments: string[] = [];
  let current: string[] = [];

  points.forEach((p, i) => {
    if (p === null) {
      if (current.length) segments.push(current.join(' '));
      current = [];
      return;
    }
    const prev = i > 0 ? points[i - 1] : null;
    if (!current.length || !prev) {
      current.push(`M ${p.x} ${p.y}`);
    } else {
      const midX = (prev.x + p.x) / 2;
      const midY = (prev.y + p.y) / 2;
      current.push(`Q ${prev.x} ${prev.y} ${midX} ${midY}`);
    }
  });
  if (current.length) segments.push(current.join(' '));

  return segments.join(' ');
};
